import * as React from 'react'

import { startOfMonth, startOfYear } from 'date-fns'
import { Control, FieldPath, FieldValues, useController } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  DatePicker,
  DateRangePicker,
  TertiaryButton,
  calendarType,
} from '@enechain/ecloud-designsystem'
import { FaCalendarRegular } from '@enechain/ecloud-icons'
import { DeliveryPeriodType } from '@enechain/eposition-proto/types/v1/deal_pb'
import {
  formatYYYYMM,
  getEndOfMonthDay,
  stringUtils,
} from '@enechain/eposition-utils'

import {
  MAX_MONTH_OFFSET,
  getFiscalYearDateRange,
  getMaxDeliveryDate,
  getMinDeliveryDate,
  getQuarterDateRange,
  getSeasonDateRange,
  getSelectableFiscalYearDates,
  getSelectableQuarterDates,
  getSelectableSeasonDates,
} from './helper'

type Props<T extends FieldValues> = {
  schemaKeyName: FieldPath<T>
  control: Control<T>
  deliveryPeriodType: DeliveryPeriodType
  handleChange?: () => void
  now?: Date
}

const CalendarAnchorElement = ({
  value,
}: {
  value: string
}): React.ReactElement => {
  const { t } = useTranslation(['common'])

  return (
    <TertiaryButton
      justifyContent="flex-start"
      width="100%"
      color="Text.highEmphasis"
      leftIcon={<FaCalendarRegular boxSize="20px" />}
      textStyle="textMd"
    >
      {stringUtils.isNotEmpty(value)
        ? value
        : t('common:form.label.unspecified')}
    </TertiaryButton>
  )
}

export const DeliveryDateRangePicker = <T extends FieldValues>({
  schemaKeyName,
  control,
  deliveryPeriodType,
  handleChange,
  now,
}: Props<T>): React.ReactElement => {
  const { field: deliveryDateRangeField } = useController({
    name: `${schemaKeyName}.deliveryDateRange` as FieldPath<T>,
    control,
  })

  const maxMonthOffset = MAX_MONTH_OFFSET

  switch (deliveryPeriodType) {
    case DeliveryPeriodType.MONTH:
      return (
        <DatePicker
          calendarType={calendarType.MONTH}
          value={deliveryDateRangeField.value?.deliveryStartDate}
          minDate={getMinDeliveryDate(now)}
          maxDate={getMaxDeliveryDate(maxMonthOffset, now)}
          onChange={(value): void => {
            deliveryDateRangeField.onChange({
              deliveryStartDate:
                value !== undefined ? startOfMonth(value) : undefined,
              deliveryEndDate:
                value !== undefined ? getEndOfMonthDay(value) : undefined,
            })
          }}
          anchorElement={
            <CalendarAnchorElement
              value={
                deliveryDateRangeField.value?.deliveryStartDate !== undefined
                  ? formatYYYYMM(
                      deliveryDateRangeField.value.deliveryStartDate,
                      '/',
                    )
                  : ''
              }
            />
          }
        />
      )
    case DeliveryPeriodType.QUARTER:
      return (
        <DatePicker
          calendarType={calendarType.MONTH}
          value={deliveryDateRangeField.value?.deliveryStartDate}
          minDate={getMinDeliveryDate(now)}
          maxDate={getMaxDeliveryDate(maxMonthOffset, now)}
          includeDates={getSelectableQuarterDates(maxMonthOffset, now)}
          onChange={(value): void => {
            const quarterDateRange = getQuarterDateRange(value)

            deliveryDateRangeField.onChange({
              deliveryStartDate:
                quarterDateRange !== undefined
                  ? quarterDateRange.deliveryStartDate
                  : undefined,
              deliveryEndDate:
                quarterDateRange !== undefined
                  ? quarterDateRange.deliveryEndDate
                  : undefined,
            })
          }}
          anchorElement={
            <CalendarAnchorElement
              value={
                deliveryDateRangeField.value?.deliveryStartDate !== undefined &&
                deliveryDateRangeField.value?.deliveryEndDate !== undefined
                  ? formatYYYYMM(
                      deliveryDateRangeField.value.deliveryStartDate,
                      '/',
                    ) +
                    ' ~ ' +
                    formatYYYYMM(
                      deliveryDateRangeField.value.deliveryEndDate,
                      '/',
                    )
                  : deliveryDateRangeField.value?.deliveryStartDate !==
                      undefined
                    ? formatYYYYMM(
                        deliveryDateRangeField.value.deliveryStartDate,
                        '/',
                      ) + ' ~ '
                    : ''
              }
            />
          }
        />
      )
    case DeliveryPeriodType.SEASON:
      return (
        <DatePicker
          calendarType={calendarType.MONTH}
          value={deliveryDateRangeField.value?.deliveryStartDate}
          minDate={getMinDeliveryDate(now)}
          maxDate={getMaxDeliveryDate(maxMonthOffset, now)}
          includeDates={getSelectableSeasonDates(maxMonthOffset, now)}
          onChange={(value): void => {
            const seasonDateRange = getSeasonDateRange(value)

            deliveryDateRangeField.onChange({
              deliveryStartDate:
                seasonDateRange !== undefined
                  ? seasonDateRange.deliveryStartDate
                  : undefined,
              deliveryEndDate:
                seasonDateRange !== undefined
                  ? seasonDateRange.deliveryEndDate
                  : undefined,
            })
          }}
          anchorElement={
            <CalendarAnchorElement
              value={
                deliveryDateRangeField.value?.deliveryStartDate !== undefined &&
                deliveryDateRangeField.value?.deliveryEndDate !== undefined
                  ? formatYYYYMM(
                      deliveryDateRangeField.value.deliveryStartDate,
                      '/',
                    ) +
                    ' ~ ' +
                    formatYYYYMM(
                      deliveryDateRangeField.value.deliveryEndDate,
                      '/',
                    )
                  : deliveryDateRangeField.value?.deliveryStartDate !==
                      undefined
                    ? formatYYYYMM(
                        deliveryDateRangeField.value.deliveryStartDate,
                        '/',
                      ) + ' ~ '
                    : ''
              }
            />
          }
        />
      )
    case DeliveryPeriodType.FISCAL_YEAR:
      return (
        <DatePicker
          calendarType={calendarType.YEAR}
          minDate={startOfYear(getMinDeliveryDate(now))}
          maxDate={getMaxDeliveryDate(maxMonthOffset, now)}
          includeDates={getSelectableFiscalYearDates(maxMonthOffset, now)}
          value={deliveryDateRangeField.value?.deliveryStartDate}
          onChange={(value): void => {
            const fiscalYearDateRange = getFiscalYearDateRange(value)

            deliveryDateRangeField.onChange({
              deliveryStartDate: fiscalYearDateRange.deliveryStartDate,
              deliveryEndDate: fiscalYearDateRange.deliveryEndDate,
            })
          }}
          anchorElement={
            <CalendarAnchorElement
              value={
                deliveryDateRangeField.value?.deliveryStartDate !== undefined &&
                deliveryDateRangeField.value?.deliveryEndDate !== undefined
                  ? formatYYYYMM(
                      deliveryDateRangeField.value.deliveryStartDate,
                      '/',
                    ) +
                    ' ~ ' +
                    formatYYYYMM(
                      deliveryDateRangeField.value.deliveryEndDate,
                      '/',
                    )
                  : deliveryDateRangeField.value?.deliveryStartDate !==
                      undefined
                    ? formatYYYYMM(
                        deliveryDateRangeField.value.deliveryStartDate,
                        '/',
                      ) + ' ~ '
                    : ''
              }
            />
          }
        />
      )
    case DeliveryPeriodType.MULTI_MONTH:
    default:
      return (
        <DateRangePicker
          calendarType={calendarType.MONTH}
          minDate={getMinDeliveryDate(now)}
          maxDate={getMaxDeliveryDate(maxMonthOffset, now)}
          from={deliveryDateRangeField.value?.deliveryStartDate}
          to={deliveryDateRangeField.value?.deliveryEndDate}
          onChange={(from?: Date, to?: Date): void => {
            const toDate = to !== undefined ? getEndOfMonthDay(to) : undefined

            deliveryDateRangeField.onChange({
              deliveryStartDate: from,
              deliveryEndDate: toDate,
            })
            if (handleChange !== undefined) {
              handleChange()
            }
          }}
          anchorElement={
            <CalendarAnchorElement
              value={
                deliveryDateRangeField.value?.deliveryStartDate !== undefined &&
                deliveryDateRangeField.value?.deliveryEndDate !== undefined
                  ? formatYYYYMM(
                      deliveryDateRangeField.value.deliveryStartDate,
                      '/',
                    ) +
                    ' ~ ' +
                    formatYYYYMM(
                      deliveryDateRangeField.value.deliveryEndDate,
                      '/',
                    )
                  : deliveryDateRangeField.value?.deliveryStartDate !==
                      undefined
                    ? formatYYYYMM(
                        deliveryDateRangeField.value.deliveryStartDate,
                        '/',
                      ) + ' ~ '
                    : ''
              }
            />
          }
        />
      )
  }
}
